import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import { EMPTY_RECEIPTS_VIEW, type CashKindFilter, type CashReceiptsIntentState, type CashReceiptsViewState } from './types'

/**
 * Отбор экрана «Чеки 1С» по каждой базе: узел дерева, период, вид чека, поиск и раскрытый чек. Сами чеки — ответ службы —
 * в кэше RTK Query (R01); отбор живёт до перезагрузки (`screen`): после F5 экран снова открывается на «сегодня» и на всей базе.
 */
const initialState: CashReceiptsIntentState = {}

/** Сколько свёрнутых узлов дерева помнит намерение. */
export const MAX_COLLAPSED = 200

const viewOf = (state: CashReceiptsIntentState, resourceId: string): CashReceiptsViewState => {
  const rid = normalizeRid(resourceId)
  state[rid] ??= { ...EMPTY_RECEIPTS_VIEW, collapsed: [] }
  return state[rid]
}

const receiptsSlice = createSlice({
  name: 'onec/receipts',
  initialState,
  reducers: {
    /** Выбор места в дереве: другой набор касс — другие чеки, раскрытый чек закрывается. */
    cashReceiptsNodeChosen(state, action: PayloadAction<{ resourceId: string; nodeId: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.nodeId = action.payload.nodeId
    },
    cashReceiptsTreeToggled(state, action: PayloadAction<{ resourceId: string; nodeId: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      const { nodeId } = action.payload
      view.collapsed = view.collapsed.includes(nodeId)
        ? view.collapsed.filter((id) => id !== nodeId)
        : [...view.collapsed, nodeId].slice(-MAX_COLLAPSED)
    },
    cashReceiptsPeriodChanged(state, action: PayloadAction<{ resourceId: string; from: string; to: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.from = action.payload.from
      view.to = action.payload.to
    },
    cashReceiptsKindChanged(state, action: PayloadAction<{ resourceId: string; kind: CashKindFilter }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.kind = action.payload.kind
    },
    /** «Обновить»: новая метка — все запросы экрана уходят заново и службе велено читать 1С, а не память. */
    cashReceiptsRefreshed(state, action: PayloadAction<{ resourceId: string; stamp: number }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.refreshStamp = action.payload.stamp
    },
    cashReceiptsCashierChanged(state, action: PayloadAction<{ resourceId: string; cashier: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.cashier = action.payload.cashier
    },
    cashReceiptsSearchChanged(state, action: PayloadAction<{ resourceId: string; search: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.search = action.payload.search
    },
  },
})

export const {
  cashReceiptsNodeChosen,
  cashReceiptsTreeToggled,
  cashReceiptsPeriodChanged,
  cashReceiptsKindChanged,
  cashReceiptsCashierChanged,
  cashReceiptsRefreshed,
  cashReceiptsSearchChanged,
} = receiptsSlice.actions
export const cashReceiptsReducer = receiptsSlice.reducer
